import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden py-1">
    <motion.div
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: EASE, delay }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

export const KineticHero = ({ showcase }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yShot = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);
  const yGlow = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const scaleShot = useTransform(scrollYProgress, [0, 1], [1, 1.05]);

  const minPrice = showcase.tiers.length ? Math.min(...showcase.tiers.map((t) => t.price)) : 0;
  const scrollToCheckout = () => document.getElementById("checkout")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="kinetic-hero" className="relative overflow-hidden">
      <motion.div style={{ y: yGlow }} className="hero-glow" aria-hidden="true" />

      <div className="max-w-6xl mx-auto px-6 pt-24 relative z-10">
        <MaskedLine delay={0.05} className="font-mono text-xs uppercase tracking-[0.35em] text-accent">
          Chapter 01 — Now Featuring
        </MaskedLine>

        <div className="mt-8 flex items-end gap-6 flex-wrap">
          <motion.img
            initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.35 }}
            src={showcase.icon_url}
            alt={showcase.app_name}
            data-testid="showcase-app-icon"
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl border border-border shadow-2xl"
          />
          <MaskedLine delay={0.18} className="font-heading text-5xl sm:text-6xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
            <span data-testid="showcase-app-name">{showcase.app_name}</span>
          </MaskedLine>
        </div>

        <MaskedLine delay={0.34} className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl">
          <span data-testid="showcase-tagline">{showcase.tagline}</span>
        </MaskedLine>

        <MaskedLine delay={0.48}>
          <div className="mt-9 flex items-center gap-5 flex-wrap">
            <button
              data-testid="hero-cta-btn"
              onClick={scrollToCheckout}
              className="group inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-8 py-3.5 text-sm font-medium transition-transform duration-300 hover:-translate-y-0.5 shadow-[0_10px_40px_-10px_hsl(22_100%_55%/0.5)]"
            >
              Unlock upgrades
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <span className="font-mono text-xs text-muted-foreground">from ${minPrice.toFixed(2)} · instant unlock · zero data stored</span>
          </div>
        </MaskedLine>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-16 pb-24 relative z-10">
        <motion.div style={{ y: yShot, scale: scaleShot }}>
          <motion.div
            initial={{ opacity: 0, y: 90 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.6 }}
            className="clip-frame"
          >
            <img
              data-testid="showcase-screenshot"
              src={showcase.screenshot_url}
              alt={`${showcase.app_name} interface`}
              className="w-full h-[300px] sm:h-[440px] object-cover"
            />
            <div className="spotlight" aria-hidden="true" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
